import React from 'react';
import { useTranslation } from 'react-i18next';

import { useTheme } from '@/theme';
import { Tag, Text } from '@/components/ui';
import { formatPhoneForDisplay } from '@/components/ui/common/PhoneInput';
import { i18nKeys } from '@/i18n/i18nKeyGen';

export interface CompanyUserDisplay {
  id: number;
  name: string;
  email: string;
  phone?: string | null;
  role_name: string;
  status: string;
  created_at?: string | Date | null;
}

interface CompanyUsersReadOnlyTableProps {
  users: CompanyUserDisplay[];
}

const formatUserDate = (date: string | Date | null | undefined): string => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

const CompanyUsersReadOnlyTable: React.FC<CompanyUsersReadOnlyTableProps> = ({ users }) => {
  const { t } = useTranslation(['auth', 'company']);
  const { theme } = useTheme();

  const cellStyle: React.CSSProperties = {
    padding: theme.spacing[4],
    textAlign: 'center',
  };

  return (
    <table
      style={{
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: theme.fontSize.sm,
      }}
    >
      <thead style={{ backgroundColor: theme.colors.surface.header.default }}>
        <tr>
          <th style={cellStyle}>{t(i18nKeys.auth.name)}</th>
          <th style={cellStyle}>{t(i18nKeys.auth.phone)}</th>
          <th style={cellStyle}>{t(i18nKeys.auth.email)}</th>
          <th style={cellStyle}>{t(i18nKeys.auth.role)}</th>
          <th style={cellStyle}>{t(i18nKeys.auth.registrationStatus)}</th>
          <th style={cellStyle}>{t(i18nKeys.auth.registrationDate)}</th>
        </tr>
      </thead>
      <tbody>
        {users.map(user => (
          <tr
            key={user.id}
            style={{ borderBottom: `1px solid ${theme.colors.border.container}` }}
          >
            <td style={cellStyle}>
              <Text size="sm">{user.name || '-'}</Text>
            </td>
            <td style={cellStyle}>
              <Text size="sm">{formatPhoneForDisplay(user.phone) || '-'}</Text>
            </td>
            <td style={cellStyle}>
              <Text size="sm">{user.email}</Text>
            </td>
            <td style={cellStyle}>
              <Tag variant="primary" size="xs">
                {user.role_name || 'Guest'}
              </Tag>
            </td>
            <td style={cellStyle}>
              <Tag variant={user.status === 'active' ? 'success' : 'error'} size="xs">
                {user.status === 'active'
                  ? t(i18nKeys.auth.active)
                  : t(i18nKeys.auth.inactive)}
              </Tag>
            </td>
            <td style={cellStyle}>
              <Text size="sm">{formatUserDate(user.created_at)}</Text>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default CompanyUsersReadOnlyTable;
